import { useStyletron } from '@thanx/uikit/theme'
import React, { useEffect, useMemo, useRef } from 'react'
import { connect, ConnectedProps } from 'react-redux'

import { Icon } from 'controlled-components'
import {
  clearBottomSelection,
  openBottom,
  selectBottomDisabled,
  selectBottomIsOpen,
  selectBottomSearchTerm,
  selectBottomSelectedItemsCount,
  selectBottomSelectedItemsData,
  selectLevel1Label,
  setBottomSearchTerm,
  toggleBottomIsOpen,
} from 'slices/aiSegmentBuilder/picker'

// ═══════════════════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════════════════

interface OwnProps {
  pickerId: number
  placeholder?: string
  disabled?: boolean
  // Data fetching props passed from parent (single source of truth)
  search?: (term: string) => void
  refetch?: () => void
  isLoading?: boolean
}

type Props = OwnProps & ConnectedProps<typeof connector>

// ═══════════════════════════════════════════════════════════════════════════
// Component
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Container for the dropdown trigger.
 * Builds the trigger UI directly using atomic components (Icon).
 * Connects to Redux for search, selection, and disabled state.
 */
const TriggerContainer: React.FC<Props> = ({
  pickerId,
  placeholder = 'Search...',
  disabled = false,
  search,
  refetch,
  isLoading = false,
  isOpen,
  searchTerm,
  selectionCount,
  selectedItemsData,
  reduxDisabled,
  level1Label,
  setSearchTerm,
  clearSelection,
  toggleIsOpen,
  open,
}) => {
  const [css, theme] = useStyletron()
  const inputRef = useRef<HTMLInputElement>(null)

  // Derived: combined disabled state (prop OR redux)
  const isDisabled = useMemo(
    () => disabled || reduxDisabled,
    [disabled, reduxDisabled]
  )

  // Derived: computed placeholder based on disabled state
  const computedPlaceholder = useMemo(
    () =>
      isDisabled
        ? `Please select ${level1Label.toLowerCase()} first`
        : placeholder,
    [isDisabled, level1Label, placeholder]
  )

  // Derived: comma-separated display text from selected items
  const displayText = useMemo(() => {
    const names = Object.values(selectedItemsData).map(item => item.name)
    return names.join(', ')
  }, [selectedItemsData])

  // Derived: show clear button
  const showClear = selectionCount > 0 && !isDisabled

  // Auto-focus input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isOpen])

  // Handlers
  const handleContainerClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (isDisabled) return
    // Don't toggle if clicking on the input or buttons
    const target = e.target as HTMLElement
    if (
      target.tagName === 'INPUT' ||
      target.tagName === 'BUTTON' ||
      target.closest('button')
    )
      return

    if (!isOpen) {
      // Opening dropdown - fetch data (with level_1 filter applied)
      refetch?.()
    }
    toggleIsOpen({ pickerId })
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setSearchTerm({ pickerId, term: value })
    search?.(value)
  }

  const handleInputFocus = () => {
    if (isDisabled) return
    if (!isOpen) {
      refetch?.()
      open({ pickerId })
    }
  }

  const handleClearClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    clearSelection({ pickerId })
  }

  // Styles
  const containerClassName = css(
    getContainerStyles(theme, { isOpen, disabled: isDisabled })
  )
  const searchIconClassName = css(getSearchIconStyles(theme))
  const inputClassName = css(getInputStyles(theme))
  const closedTextClassName = css(
    getClosedTextStyles(theme, { hasText: Boolean(displayText) })
  )
  const chevronClassName = css(getChevronStyles(theme, { isOpen }))
  const iconsContainerClassName = css(getIconsContainerStyles())
  const iconButtonClassName = css(getIconButtonStyles(theme))
  const spinnerClassName = css(getSpinnerStyles(theme))

  // Display text when closed: show displayText if provided, otherwise placeholder
  const closedDisplayText = displayText || computedPlaceholder

  return (
    <div
      role="combobox"
      aria-expanded={isOpen}
      aria-haspopup="listbox"
      onClick={handleContainerClick}
      className={containerClassName}
    >
      {/* Search icon */}
      <span className={searchIconClassName}>
        <Icon name="search" size={16} />
      </span>

      {/* When open: show search input. When closed: show selected items or placeholder */}
      {isOpen ? (
        <input
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          placeholder={computedPlaceholder}
          className={inputClassName}
        />
      ) : (
        <span className={closedTextClassName}>{closedDisplayText}</span>
      )}

      {/* Icons container */}
      <div className={iconsContainerClassName}>
        {isLoading && (
          <span className={spinnerClassName}>
            <Icon name="spinner" size={14} spin />
          </span>
        )}
        {showClear && !isLoading && (
          <button
            type="button"
            onClick={handleClearClick}
            className={iconButtonClassName}
            aria-label="Clear selection"
          >
            <Icon name="x" size={14} />
          </button>
        )}
        {/* Caret */}
        <span className={chevronClassName}>
          <Icon name="caret-down" size={16} />
        </span>
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════════
// Styles
// ═══════════════════════════════════════════════════════════════════════════

interface ContainerStyleParams {
  isOpen: boolean
  disabled: boolean
}

const getContainerStyles = (
  theme: any,
  { isOpen, disabled }: ContainerStyleParams
) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '8px 12px',
  height: '48px',
  boxSizing: 'border-box' as const,
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: disabled
    ? theme.colors.grey30
    : isOpen
    ? theme.colors.primary400
    : theme.colors.grey30,
  borderRadius: theme.borders.radius200,
  backgroundColor: disabled ? theme.colors.grey10 : theme.colors.white,
  cursor: disabled ? 'not-allowed' : 'pointer',
  transition: 'border-color 150ms ease',
  outline:
    isOpen && !disabled ? `1px solid ${theme.colors.primary400}` : 'none',
  outlineOffset: '-1px',
  opacity: disabled ? 0.7 : 1,
})

const getSearchIconStyles = (theme: any) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: theme.colors.grey50,
  flexShrink: 0,
})

const getInputStyles = (theme: any) => ({
  flex: 1,
  border: 'none',
  outline: 'none',
  fontSize: '14px',
  lineHeight: '20px',
  padding: '0',
  color: theme.colors.grey90,
  backgroundColor: 'transparent',
  '::placeholder': {
    color: theme.colors.grey50,
  },
})

interface ClosedTextStyleParams {
  hasText: boolean
}

const getClosedTextStyles = (
  theme: any,
  { hasText }: ClosedTextStyleParams
) => ({
  flex: 1,
  fontSize: '14px',
  lineHeight: '20px',
  color: hasText ? theme.colors.grey90 : theme.colors.grey50,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap' as const,
})

interface ChevronStyleParams {
  isOpen: boolean
}

const getChevronStyles = (theme: any, { isOpen }: ChevronStyleParams) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: theme.colors.grey50,
  flexShrink: 0,
  transition: 'transform 150ms ease',
  transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
})

const getIconsContainerStyles = () => ({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  flexShrink: 0,
})

const getIconButtonStyles = (theme: any) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '2px',
  border: 'none',
  background: 'none',
  cursor: 'pointer',
  color: theme.colors.grey60,
  borderRadius: '2px',
})

const getSpinnerStyles = (theme: any) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: theme.colors.grey50,
})

// ═══════════════════════════════════════════════════════════════════════════
// Redux Connection
// ═══════════════════════════════════════════════════════════════════════════

const mapStateToProps = (state: any, ownProps: OwnProps) => ({
  isOpen: selectBottomIsOpen(state, ownProps.pickerId),
  searchTerm: selectBottomSearchTerm(state, ownProps.pickerId),
  selectionCount: selectBottomSelectedItemsCount(state, ownProps.pickerId),
  selectedItemsData: selectBottomSelectedItemsData(state, ownProps.pickerId),
  reduxDisabled: selectBottomDisabled(state, ownProps.pickerId),
  level1Label: selectLevel1Label(state, ownProps.pickerId),
})

const mapDispatchToProps = {
  setSearchTerm: setBottomSearchTerm,
  clearSelection: clearBottomSelection,
  toggleIsOpen: toggleBottomIsOpen,
  open: openBottom,
}

const connector = connect(mapStateToProps, mapDispatchToProps)

export default connector(TriggerContainer)
